<template>
  <section :class="{ 'md:mt-32': isBasic }">
    <AlertBanner v-if="showErrorBanner" :status="0">
      {{ errorMessage }}
    </AlertBanner>
    <div class="max-w-448 mx-auto mt-12 flex flex-col">
      <Breadcrumb :header="breadcrumbHeader" :isButton="true" class="sm:mb-16 px-16 sm:px-0" @click="handleBreadcrumbClick" />

      <Card :elevation="3" class="messages-new-card sm:mt-16 box-shadow-none sm:box-shadow-sm sm:p-24">
        <DiscardPopup v-model="showDiscardPopup" @cancel="showDiscardPopup = false" @discard="popupDiscard" />
        <RecruitSitter
          v-if="!messageSent && isRecruitSitter"
          :account="account"
          :recipientUuid="recipientUuid"
          :recipientName="recipientName"
          @success="handleRecruitSuccess"
          @error="handleRecruitError"
          @discard-check="handleDiscardCheck"
          @continue-searching="handleContinueSearching"
        />
        <SendMessageInput
          v-else-if="!messageSent && !isBasic"
          v-model="message"
          :showCloseButton="false"
          title="Start the chat"
          :tips="tips"
          :labelName="inputLabel"
          :loading="messageSendLoading"
          @submit="sendMessage"
        />
        <RecruitJobShared v-else-if="isBasic && !messageSent" :recipient-name="recipientName" :recipient-uuid="recipientUuid" />
        <MessagesNewSent v-else :recipient-uuid="recipientUuid" />
      </Card>
    </div>
  </section>
</template>

<script lang="ts">
// messages is a shared route, but messages/new is seeker-only
import { Account } from '@/models/account';
import AlertBanner from '@/components/alert_banner/AlertBanner.vue';
import Breadcrumb from '@/components/breadcrumb/Breadcrumb.vue';
import Card from '@/components/card/Card.vue';
import DiscardPopup from '@/components/send_message_input/DiscardPopup.vue';
import SendMessageInput from '@/components/send_message_input/SendMessageInput.vue';
import RecruitSitter from '@/apps/messages/new/RecruitSitter.vue';
import MessagesNewSent from '@/apps/messages/new/MessagesNewSent.vue';
import RecruitJobShared from '@/apps/messages/new/RecruitJobShared.vue';
import { CREATE_SENDBIRD_CHANNEL } from '@/graphql/messages/mutations';
import { redirect } from '@/helpers/redirects';
import { readMeta } from '@/lib/page';

export default {
  name: 'MessagesNew',
  components: {
    AlertBanner,
    Breadcrumb,
    Card,
    DiscardPopup,
    RecruitSitter,
    SendMessageInput,
    MessagesNewSent,
    RecruitJobShared,
  },
  data() {
    return {
      recipientUuid: '',
      recipientName: '',
      returnPath: '',
      source: '',
      message: '',
      showDiscardPopup: false,
      discardEligible: false,
      messageSendLoading: false,
      messageSent: false,
      showErrorBanner: false,
      errorMessage: 'There was an error sending your message. Refresh and try again.',
    };
  },
  computed: {
    account(): Account {
      return this.$store.getters['account/getAccount'];
    },
    breadcrumbHeader(): string {
      if (this.recipientName && this.source) {
        return `Back to ${this.recipientName}'s ${this.source}`;
      }
      return 'Back';
    },
    inputLabel(): string {
      return this.recipientName ? `Start a chat with ${this.recipientName}` : 'Start a chat';
    },
    isRecruitSitter(): boolean {
      // Keep query param stable for existing routing dependencies.
      return this.$route.query?.checkAvailability === 'true';
    },
    isBasic(): boolean {
      return this.account.accountType() === 'basic';
    },
    tips(): Array<string> {
      if (this.source === 'adventure') {
        return ['Share when you need care as well as some details about your children'];
      }
      if (this.source === 'profile') {
        return [
          `Tell ${this.recipientName} why they're a good fit for your job`,
          'Ask about their availability to set up a call or an in-person interview',
        ];
      }
      return [];
    },
  },
  async mounted() {
    this.getDataFromQueryParams();
  },
  methods: {
    getDataFromQueryParams() {
      const query = this.$route?.query ?? {};
      const { uuid, displayName, return_path: returnPath, source } = query;
      this.recipientUuid = uuid != null ? String(uuid) : '';
      this.recipientName = displayName != null ? String(displayName) : '';
      this.returnPath = returnPath != null ? String(returnPath) : '';
      this.source = source != null ? String(source) : '';
    },
    handleBreadcrumbClick() {
      if ((this.message.length > 0 && !this.messageSent) || this.discardEligible) {
        this.showDiscardPopup = true;
      } else {
        this.popupDiscard();
      }
    },
    handleContinueSearching() {
      this.popupDiscard();
    },
    popupDiscard() {
      redirect(this.returnPath);
    },
    async sendMessage(message: string): Promise<void> {
      if (this.isBasic) {
        return;
      }
      this.messageSendLoading = true;
      this.showErrorBanner = false;
      this.errorMessage = 'There was an error sending your message. Refresh and try again.';

      try {
        await this.createSendbirdChannel(message);
      } catch (_error) {
        // Errors are handled in createSendbirdChannel
      }
      this.messageSendLoading = false;
    },
    handleRecruitSuccess(payload: { sentMessage: boolean }): void {
      if (this.isBasic) {
        this.messageSent = true;
        return;
      }
      this.messageSent = payload?.sentMessage === true;
    },
    handleRecruitError(message: string): void {
      this.errorMessage = message || 'There was an error sending your message. Refresh and try again.';
      this.showErrorBanner = true;
    },
    handleDiscardCheck(hasChanges: boolean): void {
      if (hasChanges) {
        this.discardEligible = true;
      } else {
        this.discardEligible = false;
      }
    },
    async createSendbirdChannel(message: string): Promise<void> {
      try {
        await this.$apollo.mutate({
          mutation: CREATE_SENDBIRD_CHANNEL,
          variables: {
            accountId: this.recipientUuid,
            message,
            session: readMeta('session'),
          },
        });
        this.messageSent = true;
      } catch (error: any) {
        const graphqlError = error.graphQLErrors?.[0];
        if (graphqlError?.extensions?.error_code === 'rate_limit_exceeded') {
          this.errorMessage = 'There was an error sending your message. Try again later.'; // TODO: Decide how we plan to handle this error in the future as per discussions
        } else {
          this.errorMessage = 'There was an error sending your message. Try again later.'; // TODO: Update this error message once the rate limiter in sendbird is reset from 25 to 50
        }
        this.showErrorBanner = true;
        throw error;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.messages-new-card {
  @media (max-width: 480px) {
    background-color: #fbfbfb !important;
  }
}
</style>
